import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Bell, MapPin, Euro, Clock, X, ExternalLink, Upload } from 'lucide-react';
import api, { isCancelError } from '../utils/axiosConfig';
import { userData } from '../utils/userData';
import { scouts } from '../utils/scouts';
import { toast } from '../utils/toast';
import { supabase } from '../utils/auth';
import { getFxRate } from '../utils/fxRate';
import { convertPrice, formatPrice } from '../utils/format';

const Profile = ({ user, onBack, onLogout, initialTab = 'profile', onProfileUpdated }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchHistory, setSearchHistory] = useState([]);
  const [searchHistoryTotal, setSearchHistoryTotal] = useState(0);
  const [loadingSearchHistory, setLoadingSearchHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userScouts, setUserScouts] = useState([]);
  const [loadingScouts, setLoadingScouts] = useState(false);
  const [saving, setSaving] = useState(false);

  // Profile form state
  const [profileData, setProfileData] = useState({
    firstname: user?.firstname || user?.name?.split(' ')[0] || '',
    lastname: user?.lastname || user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    location: user?.location || null,
    budget: user?.budget || { min: '', max: '' },
    currency_preference: user?.currency_preference || 'USD',
    area_unit_preference: user?.area_unit_preference || 'sqm',
    gender: user?.gender || '',
    dob: user?.dob || '',
    profileImage: user?.profileImage || user?.picture || null
  });

  // Location autocomplete state
  const [locationQuery, setLocationQuery] = useState(user?.location?.label || '');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const locationInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const avatarOptions = [
    { emoji: '👤', name: 'Default' },
    { emoji: '😊', name: 'Happy' },
    { emoji: '🎭', name: 'Theater' },
    { emoji: '🎨', name: 'Artist' },
    { emoji: '🎵', name: 'Music' },
    { emoji: '⚽', name: 'Sports' },
    { emoji: '📚', name: 'Bookworm' },
    { emoji: '🌍', name: 'Traveler' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab(initialTab);
  }, [initialTab]);

  // Initialize profile data from user; convert budget for display when budget_currency !== currency_preference
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      const budgetCurrency = user.budget_currency || 'USD';
      const prefCurrency = user.currency_preference || 'USD';
      let budget = user?.budget || { min: '', max: '' };

      if (budgetCurrency !== prefCurrency && (user.budget?.min != null || user.budget?.max != null)) {
        const rate = await getFxRate();
        if (rate?.EUR_to_USD) {
          const conv = (v) => (v != null && v !== '' ? convertPrice(Number(v), budgetCurrency, prefCurrency, rate.EUR_to_USD) : '');
          const converted = conv(user.budget?.min);
          const convertedMax = conv(user.budget?.max);
          if (converted != null || convertedMax != null) {
            budget = { min: converted ?? '', max: convertedMax ?? '' };
          }
        }
      }

      setProfileData({
        firstname: user?.firstname || user?.name?.split(' ')[0] || '',
        lastname: user?.lastname || user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || '',
        location: user?.location || null,
        budget,
        currency_preference: prefCurrency,
        area_unit_preference: user?.area_unit_preference || 'sqm',
        gender: user?.gender || '',
        dob: user?.dob || '',
        profileImage: user?.profile_image || user?.profileImage || user?.picture || null
      });
      setLocationQuery(user?.location?.label || '');
    };
    loadProfile().catch(() => {});
  }, [user]);

  // Get section title and subtitle based on active tab
  const getSectionInfo = (tab) => {
    switch (tab) {
      case 'searches':
        return {
          title: 'Search History',
          subtitle: 'View your past searches and results'
        };
      case 'scouts':
        return {
          title: 'My Scouts',
          subtitle: 'Manage your saved searches and alerts'
        };
      case 'profile':
        return {
          title: 'Profile Settings',
          subtitle: 'Manage your account information'
        };
      default:
        return {
          title: 'Profile Settings',
          subtitle: 'Manage your account information'
        };
    }
  };

  const sectionInfo = getSectionInfo(activeTab);

  // City normalization: map smaller cities to larger metropolitan areas
  const normalizeCity = (cityName) => {
    const cityMap = {
      'Haarlem': 'Amsterdam',
      'Amstelveen': 'Amsterdam',
      'Zaandam': 'Amsterdam',
      'Hoofddorp': 'Amsterdam',
      'Delft': 'Rotterdam',
      'Schiedam': 'Rotterdam',
      'Dordrecht': 'Rotterdam',
      'Gouda': 'Rotterdam',
      'Leiden': 'The Hague',
      'Rijswijk': 'The Hague',
      'Zoetermeer': 'The Hague',
      'Nieuwegein': 'Utrecht',
      'Amersfoort': 'Utrecht',
      'Veldhoven': 'Eindhoven',
      'Helmond': 'Eindhoven',
      'Haren': 'Groningen',
      'Assen': 'Groningen'
    };
    return cityMap[cityName] || cityName;
  };

  // Fetch Google Places Autocomplete suggestions via backend proxy
  const fetchPlacesSuggestions = useCallback(async (query, signal) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    try {
      const response = await api.get('/api/places/autocomplete', {
        params: { input: query },
        signal
      });
      
      if (!signal?.aborted && response.data.predictions) {
        setLocationSuggestions(response.data.predictions);
      } else {
        setLocationSuggestions([]);
      }
    } catch (error) {
      if (isCancelError(error)) return;
      console.error('Error fetching places:', error);
      if (!signal?.aborted) {
        setLocationSuggestions([]);
      }
    }
  }, []);

  // Calculate dropdown position when input is focused or suggestions change
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (locationInputRef.current) {
        const rect = locationInputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
    };

    if (showSuggestions) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [showSuggestions, locationSuggestions]);

  // Handle location input change with debounce
  useEffect(() => {
    if (!locationQuery || locationQuery.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      fetchPlacesSuggestions(locationQuery, abortController.signal);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [locationQuery, fetchPlacesSuggestions]);

  // Handle place selection
  const handlePlaceSelect = async (prediction) => {
    try {
      const detailsResponse = await api.post('/api/places/details', {
        place_id: prediction.place_id
      });
      
      if (detailsResponse.data.result) {
        const place = detailsResponse.data.result;
        const addressComponents = place.address_components || [];
        
        const getComponent = (type) => 
          addressComponents.find(c => c.types.includes(type));

        const cityComponent = getComponent('locality') || getComponent('administrative_area_level_2');
        const stateComponent = getComponent('administrative_area_level_1');
        const countryComponent = getComponent('country');

        const cityName = cityComponent?.long_name || place.name || prediction.description.split(',')[0];
        const normalizedCity = normalizeCity(cityName);
        
        const locationData = {
          city: normalizedCity,
          state: stateComponent?.long_name || '',
          state_code: stateComponent?.short_name || '',
          country: countryComponent?.long_name || '',
          country_code: countryComponent?.short_name || '',
          lat: place.geometry.location.lat,
          lng: place.geometry.location.lng,
          place_id: prediction.place_id,
          label: prediction.description
        };
        
        setProfileData(prev => ({ ...prev, location: locationData }));
        setLocationQuery(prediction.description);
        setShowSuggestions(false);
      }
    } catch (error) {
      // Ignore AbortError - request was cancelled intentionally
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching place details:', error);
      toast.error('Failed to fetch location details', 'Error');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData(prev => ({ ...prev, profileImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarSelect = (emoji) => {
    setProfileData(prev => ({ ...prev, profileImage: `emoji:${emoji}` }));
  };

  // Fetch scouts from API (only current user's scouts; loading always cleared)
  const fetchScouts = useCallback(async (signal) => {
    if (!user) return;

    setLoadingScouts(true);
    const timeoutMs = 15000; // stop hanging forever
    const timeoutId = setTimeout(() => {
      if (!signal?.aborted) {
        setLoadingScouts(false);
        setUserScouts(prev => (prev.length ? prev : []));
      }
    }, timeoutMs);

    try {
      const fetchedScouts = await scouts.fetchUserScouts(signal);
      if (!signal?.aborted) {
        // API already returns only current user's scouts (filtered by user_id on server)
        const list = Array.isArray(fetchedScouts) ? fetchedScouts : [];
        setUserScouts(list);
        console.log('[Profile] Scouts loaded for current user:', list.length, list);
      }
    } catch (error) {
      if (isCancelError(error)) return;
      console.error('Error fetching scouts:', error);
      if (!signal?.aborted) setUserScouts([]);
    } finally {
      clearTimeout(timeoutId);
      if (!signal?.aborted) setLoadingScouts(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const abortController = new AbortController();

    const loadData = async () => {
      setLoadingSearchHistory(true);
      try {
        const pageResult = await userData.fetchSearchHistoryPage(3, 0);
        if (!abortController.signal.aborted) {
          setSearchHistory(pageResult.items);
          setSearchHistoryTotal(pageResult.total);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingSearchHistory(false);
        }
      }
    };

    loadData().catch(() => {});

    // Fetch scouts from API
    fetchScouts(abortController.signal).catch(() => {});

    return () => {
      abortController.abort();
    };
  }, [user, fetchScouts]);

  const handleDeleteScout = async (scoutId) => {
    const confirmed = window.confirm('Are you sure you want to delete this scout? You will stop receiving alerts for this search.');
    if (confirmed) {
      try {
        await scouts.delete(scoutId);
        await fetchScouts();
      } catch (error) {
        console.error('Error deleting scout:', error);
        alert('Failed to delete scout. Please try again.');
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSaving(true);
    
    // Get the correct user ID for the API call
    // The server expects auth_user_id (Supabase auth user ID), not the database ID
    let userId = user.auth_user_id || user.id;
    
    // If auth_user_id is not available, try to get it from the Supabase session
    if (!user.auth_user_id && supabase) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.user?.id) {
          userId = sessionData.session.user.id;
          console.log('Using auth user ID from Supabase session:', userId);
        }
      } catch (sessionError) {
        console.warn('Could not get session for user ID:', sessionError);
      }
    }
    
    if (!userId) {
      setSaving(false);
      toast.error('Unable to determine user ID. Please log out and log back in.', 'Error');
      return;
    }
    
    try {

      // Format date of birth if provided
      const formattedDob = profileData.dob ? new Date(profileData.dob).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }) : '';

      // Prepare update data - database only has 'name' field (not firstname/lastname)
      // Combine firstname and lastname into a single 'name' field
      const fullName = `${profileData.firstname || ''} ${profileData.lastname || ''}`.trim();
      const updateData = {
        name: fullName || user.name
      };

      // Only add fields that have values
      // location and city are both jsonb fields in the database
      if (profileData.location && typeof profileData.location === 'object' && Object.keys(profileData.location).length > 0) {
        updateData.location = profileData.location;
        // city field can store the city from location object if needed
        if (profileData.location.city) {
          updateData.city = { city: profileData.location.city };
        }
        // Also persist top-level fields used by the server (e.g. Pinecone namespace derivation).
        // These come from Google Places details in handlePlaceSelect().
        if (profileData.location.country) {
          updateData.country = profileData.location.country;
        }
        if (profileData.location.state_code) {
          updateData.state_code = profileData.location.state_code;
        }
      }
      if (profileData.budget && typeof profileData.budget === 'object') {
        // Only include budget if it has at least one value; store in current preference currency
        if (profileData.budget.min || profileData.budget.max) {
          updateData.budget = {
            min: profileData.budget.min || null,
            max: profileData.budget.max || null
          };
          updateData.budget_currency = profileData.currency_preference || 'USD';
        }
      }
      if (profileData.currency_preference) {
        updateData.currency_preference = profileData.currency_preference;
      }
      if (profileData.area_unit_preference) {
        updateData.area_unit_preference = profileData.area_unit_preference;
      }
      if (profileData.gender?.trim()) {
        updateData.gender = profileData.gender.trim();
      }
      if (formattedDob) {
        updateData.dob = formattedDob;
      }
      if (profileData.profileImage) {
        updateData.profile_image = profileData.profileImage;
      }
      
      console.log('=== Profile Update Request ===');
      console.log('User object:', {
        id: user.id,
        email: user.email,
        name: user.name,
        auth_user_id: user.auth_user_id,
        authProvider: user.authProvider,
        userIdToUse: userId
      });
      console.log('Sending PATCH to:', `/api/users/${userId}`);
      console.log('Update data:', { ...updateData, profile_image: updateData.profile_image?.substring(0, 50) + '...' });

      // Update database via API - use auth_user_id (Supabase auth ID)
      const response = await api.patch(`/api/users/${userId}`, updateData);
      
      // Update local user state with response data
      if (response.data?.user) {
        onProfileUpdated?.(response.data.user);
      }

      toast.success('Profile updated successfully!', 'Success');
      
      // Redirect to root/search page after successful save
      if (onBack) {
        // Small delay to show success message
        setTimeout(() => {
          onBack();
        }, 500);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Request URL:', error.config?.url);
      console.error('User ID used:', userId);
      
      // Show more detailed error message
      let errorMessage = 'Failed to save profile. Please try again.';
      
      if (error.response?.status === 404) {
        errorMessage = 'User not found. Please try logging out and back in.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check if it's a timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      }
      
      toast.error(errorMessage, 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-surface">
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-8">
        {/* Content */}
        <AnimatePresence mode="wait">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#17252A] mb-1">{sectionInfo.title}</h1>
                <p className="text-sm text-[#17252A]/60">{sectionInfo.subtitle}</p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-[#9c795b]/10 shadow-sm">
                <div className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">First Name</label>
                      <input
                        type="text"
                        value={profileData.firstname}
                        onChange={(e) => setProfileData(prev => ({ ...prev, firstname: e.target.value }))}
                        className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Last Name</label>
                      <input
                        type="text"
                        value={profileData.lastname}
                        onChange={(e) => setProfileData(prev => ({ ...prev, lastname: e.target.value }))}
                        className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Email</label>
                    <input
                      type="email"
                      value={profileData.email}
                      disabled
                      className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl bg-[#17252A]/5 text-[#17252A]/40"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Where are you looking for housing?</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#17252A]/30" />
                      <input
                        ref={locationInputRef}
                        type="text"
                        value={locationQuery}
                        onChange={(e) => {
                          setLocationQuery(e.target.value);
                          if (profileData.location) {
                            setProfileData(prev => ({ ...prev, location: null }));
                          }
                          setShowSuggestions(true);
                          if (locationInputRef.current) {
                            const rect = locationInputRef.current.getBoundingClientRect();
                            setDropdownPosition({
                              top: rect.bottom + 4,
                              left: rect.left,
                              width: rect.width
                            });
                          }
                        }}
                        onFocus={() => {
                          if (locationSuggestions.length > 0) setShowSuggestions(true);
                          if (locationInputRef.current) {
                            const rect = locationInputRef.current.getBoundingClientRect();
                            setDropdownPosition({
                              top: rect.bottom + 4,
                              left: rect.left,
                              width: rect.width
                            });
                          }
                        }}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        placeholder="e.g. Amsterdam, New York, London..."
                        className="w-full pl-10 pr-10 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                      />
                      {profileData.location && (
                        <button
                          type="button"
                          onClick={() => {
                            setProfileData(prev => ({ ...prev, location: null }));
                            setLocationQuery('');
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#17252A]/30 hover:text-[#17252A]/60"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                      
                      {typeof document !== 'undefined' && createPortal(
                        <AnimatePresence>
                          {showSuggestions && locationSuggestions.length > 0 && (
                            <motion.div
                              key="location-dropdown"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="fixed z-[60] bg-white border-2 border-gray-200 rounded-lg shadow-2xl max-h-60 overflow-y-auto"
                              style={{
                                top: `${dropdownPosition.top || 0}px`,
                                left: `${dropdownPosition.left || 0}px`,
                                width: `${dropdownPosition.width || (locationInputRef.current?.offsetWidth || 400)}px`
                              }}
                            >
                              {locationSuggestions.map((prediction) => (
                                <button
                                  key={prediction.place_id}
                                  type="button"
                                  onClick={() => handlePlaceSelect(prediction)}
                                  onMouseDown={(e) => e.preventDefault()}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 first:rounded-t-lg last:rounded-b-lg"
                                >
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-[#17252A]/30 flex-shrink-0" />
                                    <div>
                                      <div className="font-medium text-gray-900">{prediction.structured_formatting?.main_text || prediction.description}</div>
                                      {prediction.structured_formatting?.secondary_text && (
                                        <div className="text-sm text-gray-500 mt-0.5">{prediction.structured_formatting.secondary_text}</div>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>,
                        document.body
                      )}
                    </div>
                  </div>

                  {/* Preferences + Budget */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Preferences</label>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-[#17252A]/50 mb-1">Currency</label>
                          <div className="grid grid-cols-2 gap-3">
                            {['USD', 'EUR'].map((curr) => (
                              <motion.button
                                key={curr}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setProfileData(prev => ({ ...prev, currency_preference: curr }))}
                                className={`p-3 rounded-lg border-2 font-medium transition-all ${
                                  profileData.currency_preference === curr
                                    ? 'border-[#9c795b] bg-[#9c795b] text-white'
                                    : 'border-[#17252A]/10 bg-white text-[#17252A] hover:border-[#9c795b]/50'
                                }`}
                              >
                                {curr === 'USD' ? '$ Dollars' : '€ Euros'}
                              </motion.button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs text-[#17252A]/50 mb-1">Area unit</label>
                          <div className="grid grid-cols-2 gap-3">
                            {['sqm', 'sqft'].map((unit) => (
                              <motion.button
                                key={unit}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setProfileData(prev => ({ ...prev, area_unit_preference: unit }))}
                                className={`p-3 rounded-lg border-2 font-medium transition-all ${
                                  profileData.area_unit_preference === unit
                                    ? 'border-[#9c795b] bg-[#9c795b] text-white'
                                    : 'border-[#17252A]/10 bg-white text-[#17252A] hover:border-[#9c795b]/50'
                                }`}
                              >
                                {unit === 'sqm' ? 'm² (square meters)' : 'sqft (square feet)'}
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Budget</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-[#17252A]/50 mb-1">
                            Min ({profileData.currency_preference === 'EUR' ? '€' : '$'})
                          </label>
                          <input
                            type="number"
                            value={profileData.budget.min}
                            onChange={(e) => setProfileData(prev => ({
                              ...prev,
                              budget: { ...prev.budget, min: e.target.value }
                            }))}
                            className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                            placeholder="800"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#17252A]/50 mb-1">
                            Max ({profileData.currency_preference === 'EUR' ? '€' : '$'})
                          </label>
                          <input
                            type="number"
                            value={profileData.budget.max}
                            onChange={(e) => setProfileData(prev => ({
                              ...prev,
                              budget: { ...prev.budget, max: e.target.value }
                            }))}
                            className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                            placeholder="1500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Gender</label>
                    <div className="grid grid-cols-3 gap-3">
                      {['Male', 'Female', 'Other'].map((gender) => (
                        <motion.button
                          key={gender}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setProfileData(prev => ({ ...prev, gender }))}
                          className={`p-3 rounded-lg border-2 font-medium transition-all ${
                            profileData.gender === gender
                              ? 'border-[#9c795b] bg-[#9c795b] text-white'
                              : 'border-[#17252A]/10 bg-white text-[#17252A] hover:border-[#9c795b]/50'
                          }`}
                        >
                          {gender}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Date of Birth</label>
                    <input
                      type="date"
                      value={profileData.dob}
                      onChange={(e) => setProfileData(prev => ({ ...prev, dob: e.target.value }))}
                      className="w-full px-4 py-3 border border-[#17252A]/10 rounded-xl focus:outline-none focus:border-[#9c795b]"
                    />
                  </div>

                  {/* Profile Image */}
                  <div>
                    <label className="block text-sm font-semibold text-[#17252A]/60 mb-2">Profile Picture</label>
                    
                    {/* Current Image Preview */}
                    {profileData.profileImage && !profileData.profileImage.startsWith('emoji:') && (
                      <div className="flex justify-center mb-4">
                        <div className="relative">
                          <img
                            src={profileData.profileImage}
                            alt="Profile"
                            className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                          />
                          <button
                            onClick={() => setProfileData(prev => ({ ...prev, profileImage: null }))}
                            className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Emoji Avatar Preview */}
                    {profileData.profileImage && profileData.profileImage.startsWith('emoji:') && (
                      <div className="flex justify-center mb-4">
                        <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center text-5xl border-4 border-gray-200">
                          {profileData.profileImage.replace('emoji:', '')}
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="mb-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full px-4 py-3 border-2 border-[#17252A]/10 rounded-xl font-medium text-[#17252A] hover:border-[#9c795b] transition-all flex items-center justify-center gap-2"
                      >
                        <Upload className="w-5 h-5" />
                        Upload Photo
                      </motion.button>
                    </div>

                    <div className="text-center text-sm text-[#17252A]/50 mb-3">or</div>

                    {/* Avatar Selection */}
                    <div>
                      <label className="block text-xs text-[#17252A]/50 mb-3">Choose an Avatar</label>
                      <div className="grid grid-cols-4 gap-3">
                        {avatarOptions.map((avatar) => (
                          <motion.button
                            key={avatar.name}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleAvatarSelect(avatar.emoji)}
                            className={`p-4 rounded-lg border-2 text-3xl transition-all ${
                              profileData.profileImage === `emoji:${avatar.emoji}`
                                ? 'border-[#9c795b] bg-[#9c795b]/10'
                                : 'border-[#17252A]/10 bg-white hover:border-[#9c795b]/50'
                            }`}
                          >
                            {avatar.emoji}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className="w-full px-6 py-3 bg-[#9c795b] text-white rounded-xl font-semibold hover:bg-[#8a6b4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save Changes</span>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Search History */}
          {activeTab === 'searches' && (
            <motion.div
              key="searches"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#17252A] mb-1">{sectionInfo.title}</h1>
                <p className="text-sm text-[#17252A]/60">{sectionInfo.subtitle}</p>
              </div>

              {loadingSearchHistory ? (
                <div className="bg-white rounded-xl p-12 text-center border border-[#9c795b]/10">
                  <p className="text-[#17252A]/60">Loading search history...</p>
                </div>
              ) : searchHistory.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center border border-[#9c795b]/10">
                  <Search className="w-12 h-12 text-[#17252A]/20 mx-auto mb-4" />
                  <p className="text-[#17252A]/60">No search history yet</p>
                </div>
              ) : (
                <>
                  {searchHistory.map((search) => (
                    <motion.div
                      key={search.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white rounded-xl p-6 border border-[#9c795b]/10 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-[#17252A]/40" />
                            <span className="text-xs text-[#17252A]/40">
                              {new Date(search.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="font-semibold text-[#17252A] mb-2">{search.query || 'Filtered Search'}</p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {search.filters?.city && (
                              <span className="px-2 py-1 bg-[#9c795b]/10 text-[#9c795b] rounded text-xs font-semibold">
                                {search.filters.city}
                              </span>
                            )}
                            {search.filters?.maxPrice && (
                              <span className="px-2 py-1 bg-[#9c795b]/10 text-[#9c795b] rounded text-xs font-semibold">
                                Max {user?.currency_preference === 'EUR' ? '€' : '$'}{search.filters.maxPrice}
                              </span>
                            )}
                            {search.filters?.rooms && (
                              <span className="px-2 py-1 bg-[#9c795b]/10 text-[#9c795b] rounded text-xs font-semibold">
                                {search.filters.rooms}+ rooms
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#17252A]/60 mb-3">
                            Found <span className="font-semibold text-[#9c795b]">{search.resultCount}</span> properties
                          </p>
                          {/* Top Results Preview */}
                          {search.topResults && search.topResults.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#17252A]/5">
                              <p className="text-xs font-semibold text-[#17252A]/60 mb-2 uppercase tracking-wide">Top Results</p>
                              <div className="space-y-2">
                                {search.topResults.map((result, idx) => (
                                  <motion.div
                                    key={idx}
                                    whileHover={{ x: 4 }}
                                    className="flex items-center justify-between p-2 bg-[#17252A]/5 rounded-lg hover:bg-[#9c795b]/10 transition-colors cursor-pointer group"
                                    onClick={() => {
                                      if (result.url) {
                                        window.open(result.url, '_blank');
                                      }
                                    }}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-[#17252A] truncate">{result.title}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-[#9c795b] font-semibold">{formatPrice({ price: result.price, currency: result.currency }, user?.currency_preference, null)}</span>
                                        <span className="text-xs text-[#17252A]/40">•</span>
                                        <span className="text-xs text-[#17252A]/60">{result.city}</span>
                                      </div>
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-[#17252A]/40 group-hover:text-[#9c795b] transition-colors flex-shrink-0" />
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  {searchHistory.length < searchHistoryTotal && (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        onClick={async () => {
                          setLoadingMore(true);
                          try {
                            const { items, total } = await userData.fetchSearchHistoryPage(3, searchHistory.length);
                            setSearchHistory(prev => [...prev, ...items]);
                            setSearchHistoryTotal(total);
                          } finally {
                            setLoadingMore(false);
                          }
                        }}
                        disabled={loadingMore}
                        className="px-4 py-2 rounded-xl border border-[#9c795b]/30 text-[#9c795b] font-medium hover:bg-[#9c795b]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loadingMore ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* Scouts */}
          {activeTab === 'scouts' && (
            <motion.div
              key="scouts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-[#17252A] mb-1">{sectionInfo.title}</h1>
                <p className="text-sm text-[#17252A]/60">{sectionInfo.subtitle}</p>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-[#17252A]">My Scouts</h3>
                  <p className="text-sm text-[#17252A]/60">
                    {userScouts.length}/3 scouts active
                  </p>
                </div>
                {userScouts.length >= 3 && (
                  <div className="px-4 py-2 bg-[#EF4444]/10 text-[#EF4444] rounded-xl text-sm font-semibold">
                    Limit reached
                  </div>
                )}
              </div>

              {loadingScouts ? (
                <div className="bg-white rounded-xl p-12 text-center border border-[#9c795b]/10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9c795b] mx-auto mb-4"></div>
                  <p className="text-[#17252A]/60">Loading scouts...</p>
                </div>
              ) : userScouts.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center border border-[#9c795b]/10">
                  <Bell className="w-12 h-12 text-[#17252A]/20 mx-auto mb-4" />
                  <p className="text-[#17252A]/60 mb-2">No active scouts</p>
                  <p className="text-sm text-[#17252A]/40">Save a search to create your first scout!</p>
                </div>
              ) : (
                userScouts.map((scout) => {
                  const aiDetails = scout.aiExtracted || scout.ai_extracted || {};
                  const searchableDesc = aiDetails.searchableDescription || scout.query || scout.name;
                  
                  return (
                    <motion.div
                      key={scout.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white rounded-xl p-6 border border-[#9c795b]/10 shadow-sm hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Bell className="w-5 h-5 text-[#9c795b]" />
                            <h4 className="font-bold text-[#17252A]">{scout.name}</h4>
                          </div>
                          
                          {searchableDesc && (
                            <p className="text-sm text-[#17252A]/70 mb-3 font-medium">{searchableDesc}</p>
                          )}
                          
                          {scout.query && scout.query !== searchableDesc && (
                            <p className="text-xs text-[#17252A]/50 mb-3 italic">"{scout.query}"</p>
                          )}
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {scout.filters?.city || aiDetails.city ? (
                              <span className="px-3 py-1.5 bg-[#9c795b]/10 text-[#9c795b] rounded-lg text-xs font-semibold flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {scout.filters?.city || aiDetails.city}
                              </span>
                            ) : null}
                            {(scout.filters?.maxPrice || aiDetails.maxPrice) && (
                              <span className="px-3 py-1.5 bg-[#9c795b]/10 text-[#9c795b] rounded-lg text-xs font-semibold flex items-center gap-1">
                                <Euro className="w-3 h-3" />
                                Max {user?.currency_preference === 'EUR' ? '€' : '$'}{scout.filters?.maxPrice || aiDetails.maxPrice}
                              </span>
                            )}
                            {(scout.filters?.minSqm || aiDetails.minSqm) && (
                              <span className="px-3 py-1.5 bg-[#9c795b]/10 text-[#9c795b] rounded-lg text-xs font-semibold">
                                Min {user?.area_unit_preference === 'sqft'
                                  ? `${Math.round((scout.filters?.minSqm || aiDetails.minSqm) * 10.7639)} sqft`
                                  : `${scout.filters?.minSqm || aiDetails.minSqm} m²`}
                              </span>
                            )}
                            {(scout.filters?.rooms || aiDetails.rooms) && (
                              <span className="px-3 py-1.5 bg-[#9c795b]/10 text-[#9c795b] rounded-lg text-xs font-semibold">
                                {scout.filters?.rooms || aiDetails.rooms}+ rooms
                              </span>
                            )}
                            {aiDetails.semanticKeywords && aiDetails.semanticKeywords.length > 0 && (
                              <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold">
                                {aiDetails.semanticKeywords.slice(0, 2).join(', ')}
                              </span>
                            )}
                          </div>
                          
                          {(aiDetails.mustHave?.length > 0 || aiDetails.propertyType || aiDetails.neighborhoodType) && (
                            <div className="mt-3 pt-3 border-t border-[#17252A]/5">
                              {aiDetails.propertyType && (
                                <p className="text-xs text-[#17252A]/50 mb-1">
                                  <span className="font-semibold">Type:</span> {aiDetails.propertyType}
                                </p>
                              )}
                              {aiDetails.neighborhoodType && (
                                <p className="text-xs text-[#17252A]/50 mb-1">
                                  <span className="font-semibold">Area:</span> {aiDetails.neighborhoodType}
                                </p>
                              )}
                              {aiDetails.mustHave && aiDetails.mustHave.length > 0 && (
                                <p className="text-xs text-[#17252A]/50">
                                  <span className="font-semibold">Must have:</span> {aiDetails.mustHave.join(', ')}
                                </p>
                              )}
                            </div>
                          )}
                          
                          <p className="text-xs text-[#17252A]/40 mt-3">
                            Created {new Date(scout.createdAt || scout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDeleteScout(scout.id)}
                          className="p-2 hover:bg-[#EF4444]/10 rounded-lg transition-colors"
                          title="Delete scout"
                        >
                          <X className="w-5 h-5 text-[#EF4444]" />
                        </motion.button>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Profile;
